import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import {
  Address,
  Image,
  ImagePath,
  ReportValue,
  UniversityUserRole,
  User,
  UserAbstract,
  UserService as UserApiService,
  UserJob,
  UserList,
} from '@sciper/api-client'
import {
  BehaviorSubject,
  first,
  merge,
  mergeAll,
  Observable,
  of,
  shareReplay,
  Subject,
  take,
  takeUntil,
} from 'rxjs'
import { environment } from 'src/environments/environment'
import {
  AbstractRestService,
  RestServiceStatus,
} from '../abstract-rest/abstract-rest.service'
import { AddressService } from '../address/address.service'
import { AuthService } from '../auth/auth.service'
import { ImageService } from '../image/image.service'
import { ReportValueData } from './user.service.fixtures'

type UserSearchOptions = {
  userId?: string
  mail?: string
  firstname?: string
  lastname?: string
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends AbstractRestService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()
  private uuid?: string = undefined

  ownUser = new BehaviorSubject<User>(undefined)

  user = new BehaviorSubject<User>(undefined)
  users = new BehaviorSubject<UserAbstract[]>(undefined)
  universityUserRoles = new BehaviorSubject<UniversityUserRole[]>(undefined)

  get ownUserId(): string {
    return this.uuid
  }

  constructor(
    private userApiService: UserApiService,
    private addressService: AddressService,
    private imageService: ImageService,
    private httpClient: HttpClient,
    private authService: AuthService
  ) {
    super()
    authService
      .getUserProfile()
      .pipe(takeUntil(this._destroySubject$))
      ?.subscribe((user) => {
        if (user) {
          this.uuid = user.uuid
          this.fetchOwnUser()
          this.fetchUserUniversityRole(this.uuid)
        }
      })
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  fetchOwnUser() {
    this.fetchSingleUser(this.ownUserId, true)
  }

  fetchUser(userId: string) {
    this.fetchSingleUser(userId, false)
  }

  fetchUserListWithOptions(searchOptions: UserSearchOptions) {
    const searchString = this.getSearchString(searchOptions)
    this.fetchUserList(searchString)
  }

  fetchUserList(searchString: string) {
    this.status.next(RestServiceStatus.Loading)
    this.userApiService
      .getUserList(searchString)
      .pipe(take(1))
      .subscribe(
        (users) => {
          if (users) {
            const userList = users as UserList
            this.users.next(userList.items)
            this.status.next(RestServiceStatus.Success)
          } else {
            this.status.next(RestServiceStatus.Error)
            this.errorMessage.next('error.loading')
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
            this.errorMessage.next('error.networkLoading')
          } else {
            this.status.next(RestServiceStatus.Error)
            this.errorMessage.next('error.loading')
          }
        }
      )
  }

  private fetchSingleUser(userId: string, isOwnUser: boolean) {
    if (!userId) {
      return
    }

    this.status.next(RestServiceStatus.Loading)
    this.userApiService
      .getUser(userId)
      .pipe(take(1))
      .subscribe(
        (user) => {
          if (isOwnUser) {
            this.ownUser.next(user)
          } else {
            this.user.next(user)
          }
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
            this.errorMessage.next('error.networkLoading')
          } else {
            this.status.next(RestServiceStatus.Error)
            this.errorMessage.next('error.loading')
          }
        }
      )
  }

  private getSearchString(
    searchOptions: UserSearchOptions,
    combine: 'and' | 'or' = 'and'
  ): string {
    const searchArray: string[] = []
    if (searchOptions.mail) {
      searchArray.push(`email == '${searchOptions.mail}'`)
    }
    if (searchOptions.userId) {
      searchArray.push(`userId == '${searchOptions.userId}'`)
    }
    if (searchOptions.firstname) {
      searchArray.push(`firstname == '${searchOptions.firstname}'`)
    }
    if (searchOptions.lastname) {
      searchArray.push(`lastname == '${searchOptions.lastname}'`)
    }
    return searchArray.join(combine)
  }

  fetchUserUniversityRole(userId: string) {
    this.userApiService
      .getUserUniversityRole(userId)
      .pipe(take(1))
      .subscribe((universityRoles) => {
        this.universityUserRoles.next(universityRoles)
      })
  }

  createUniversityRole(
    userId: string,
    universityId: string,
    role: UniversityUserRole.RoleEnum = UniversityUserRole.RoleEnum.Employee
  ): Observable<any> {
    return this.userApiService.createUserUniversityRole(
      userId,
      universityId,
      role
    )
  }

  deleteUniversityRole(
    userId: string,
    universityId: string,
    role: UniversityUserRole.RoleEnum = UniversityUserRole.RoleEnum.Employee
  ): Observable<any> {
    return this.userApiService.deleteUserUniversityRole(
      userId,
      universityId,
      role
    )
  }

  updateUserAddress(user: User, address: Address) {
    if (user.address?.addressId) {
      // update existing address
      this.addressService.updateAddress(user.address?.addressId, address)
    } else {
      // create new address
      this.addressService.createAddress(address, (success, addressId) => {
        setTimeout(() => {
          if (success) {
            this.userApiService
              .createUserAddress(user.userId, addressId)
              .pipe(take(1))
              .subscribe(() => {
                // nothing to do
              })
          }
        }, 1000)
      })
    }
  }

  deleteUserAddress(user: User) {
    const userWithoutAddress = user
    userWithoutAddress.address = undefined
    this.userApiService
      .updateUser(user.userId, userWithoutAddress)
      .pipe(take(1))
      .subscribe(() => {
        this.userApiService.deleteUserAddress(
          user.userId,
          user.address?.addressId
        )
      })
  }

  updateUserAvatar(user: User, base64: string, format: string) {
    const file = this.imageService.createFileFromPhoto(
      base64,
      format,
      user.userId
    )
    if (!file) {
      return
    }
    this.imageService.createImage(
      user.userId,
      file,
      (success: boolean, imagePath: ImagePath) => {
        if (success && imagePath) {
          const image: Image = {
            alt: user.userId,
            url: environment.apiBasePath + imagePath.imagePath,
          }

          user.avatarUrl = image.url
          this.userApiService
            .updateUser(this.uuid, user)
            .pipe(first())
            .subscribe((_) => this.fetchOwnUser())
        }
      }
    )
  }

  /**
   * Updates user using keycloak-api
   * @param user
   * @returns
   */
  updateUser(user: User): Observable<any> {
    const newData = {
      id: user.userId,
      email: user.email,
      firstName: user.firstname,
      lastName: user.lastname,
    }
    const token = this.authService.getAccessToken()
    return this.httpClient.post(
      environment.oauthConfig.userAccountUri,
      newData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    )
  }

  createReportValues(reportValueData: ReportValueData[]) {
    if (!this.uuid) {
      return
    }
    const reportValues: ReportValue[] = reportValueData.map(
      ({ questionId, answerId, value }) => {
        return {
          questionId: questionId,
          userId: this.uuid,
          numValue: typeof value === 'number' ? value : undefined,
          textValue: typeof value === 'string' ? value : undefined,
          uuidValue: answerId,
        }
      }
    )
    const questionIds = reportValues.map((r) => r.questionId)
    console.log('createReportValues', questionIds)
    this.deleteReportValues(questionIds)
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.userApiService
            .createReportValue(reportValues)
            .pipe(take(1))
            .subscribe(() => {
              this.fetchOwnUser()
              })
        },
        complete: () =>
          this.userApiService
            .createReportValue(reportValues)
            .pipe(take(1))
            .subscribe(() => {
              this.fetchOwnUser()
            })
      })
  }

  createReportValue(reportValueData: ReportValueData) {
    this.createReportValues([reportValueData])
  }

  deleteReportValues(questionIds: string[]): Observable<boolean> {
    const reportValues = questionIds
      .flatMap((questionId) =>
        this.ownUser.value?.reportValueList.filter(
          (r) => r.questionId === questionId
        )
      )
      .filter(Boolean)

    if (!reportValues || reportValues.length === 0) {
      return of(true)
    }

    const deleteObservable = merge(
      ...reportValues.map((reportValue) =>
        this.userApiService.deleteReportValue(reportValue?.reportValueId).pipe(shareReplay(1))
      )
    )

    deleteObservable.subscribe({
      complete: () => {
        this.fetchOwnUser()
      },
      error: () => {
        this.fetchOwnUser()
      }
    })

    return deleteObservable
  }

  deleteReportValue(questionId: string): Observable<boolean> {
    return this.deleteReportValues([questionId])
  }

  createUserJob(jobId: string, isFavourite: boolean) {
    const data: UserJob = {
      userJobId: undefined,
      jobId,
      userId: this.uuid,
      favourite: isFavourite,
    }
    this.userApiService
      .createUserJob(data)
      .pipe(first())
      .subscribe((_) => {
        // nothing to do
      })
  }
}
